import "@shared/ui/styles.css";
import "./styles.css";
import "./stockforgeBridge.css";
import "./analyticsWeb.css";
import { mountFilterResetButtons } from "@shared/ui/filterReset";
import { mountHeaderTableSort } from "@shared/ui/tableSort";
import { openProxySettingsModal, startProxyHealthPolling } from "@shared/ui/panel";
import {
  activateWebTab,
  deactivateWebTab,
  refreshActiveDataTab,
  type WebTabId,
} from "./webTabs";
import { invalidateOptionsPanelCache, refreshNativeOptionsPanelInBackground } from "./nativeOptionsPanel";
import { refreshNativePutsPanelInBackground } from "./nativePutsScreener";
import { refreshNativeCallsPanelInBackground } from "./nativeCallsScreener";
import { refreshNativeStocksPanelInBackground } from "./nativeStocksPanel";
import { createStickySync } from "./stickyTabNav";
import { applyProxyHealth, proxyHealthIndicatorHtml } from "@shared/ui/toolbar";
import {
  applyRobinhoodStatus,
  robinhoodStatusIndicatorHtml,
  startRobinhoodStatusPolling,
} from "@shared/ui/robinhoodStatus";
import {
  applyWebullStatus,
  webullStatusIndicatorHtml,
  startWebullStatusPolling,
} from "@shared/ui/webullStatus";
import {
  applyAgentsStatus,
  agentsStatusIndicatorHtml,
  markAgentsPageVisited,
  startAgentsStatusPolling,
} from "@shared/ui/agentsStatus";
import { marketStatusIndicatorHtml, startMarketStatusPolling } from "@shared/ui/marketStatusIndicator";
import { bindAccountScopeBar } from "@shared/ui/accountScopeBar";
import { bindScrollEdges } from "@shared/ui/scrollEdges";
import { paperAccountsEnabled, setAccountScopeSurface, subscribeAccountScope } from "@shared/ui/accountScope";
import {
  headerFeedIndicatorHtml,
  startHeaderFeedIndicator,
} from "@shared/ui/headerFeedIndicator";
import {
  headerBuyingPowerHtml,
  refreshHeaderBuyingPower,
  refreshHeaderBuyingPowerLive,
  startHeaderBuyingPower,
} from "@shared/ui/headerBuyingPower";
import {
  isTabVisible,
  loadWebTabPreferences,
  loadWebLastTab,
  migrateWebTabId,
  resolveDefaultTabId,
  saveWebLastTab,
  WEB_TAB_PREF_DEFINITIONS,
  type TabPreferences,
} from "@shared/ui/tabPreferences";
import { filterAgenticTab, IMPORT_TAB_ID, isAgenticFeatureEnabled, isGatedTabId, isRobinhoodIntegrationEnabled, loadAgenticFeatureFlag } from "@shared/ui/agenticFeatures";
import {
  isWebCompactMode,
  setWebCompactMode,
  syncWebCompactBodyClass,
} from "@shared/ui/webCompactMode";
import { syncTableShadingBodyClass } from "@shared/ui/tableShading";
import {
  bindFocusRowMenuDocumentHandlers,
  setFocusActionsRefresh,
} from "@shared/ui/focusActions";
import { setWheelLedgerRefresh } from "@shared/ui/wheelActions";
import { mountTableContextMenu } from "@shared/ui/tableContextMenu";
import { bindStockForgeTickerLinks } from "@shared/ui/stockForgeReport";
import { warmRobinhoodNetwork } from "@shared/ui/robinhoodOpen";
import { applyDesktopShellChrome } from "@shared/ui/desktopShell";
import { invalidateWheelSnapshots } from "@shared/ui/watchlistData";
import {
  WEB_TAB_BAR,
  applyWheelTabButtonClasses,
  bodyAttrForTab,
  findWebTabDef,
  renderSiteHeaderHtml,
  renderSiteFooterHtml,
  tabButtonAttrs,
  tabButtonClasses,
  tabButtonInnerHtml,
  visibleWebTabs,
} from "./wheelChrome";
import { openWebSettingsModal } from "./webSettingsModal";
import { openWebLoginModal, consumeAuthDeepLink, consumeDesktopPairLink } from "./webLoginModal";
import { isSessionAuthenticated, notifyAuthChanged, registerAuthUiHooks } from "./authUi";
import { fetchAuthMe, logoutSession } from "@shared/ui/sessionAuth";
import { bindTaxStoreUser } from "@shared/ui/tax";
import { webCompactLayoutIcon } from "./toolbarIcons";

async function syncTaxStoreToSession(): Promise<void> {
  try {
    const me = await fetchAuthMe();
    bindTaxStoreUser(me.authenticated ? me.user?.id ?? null : null);
  } catch {
    bindTaxStoreUser(null);
  }
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function renderTabStripHtml(tabPrefs: TabPreferences, activeTab: WebTabId): string {
  return visibleWebTabs(tabPrefs)
    .map(
      (tab) =>
        `<button ${tabButtonAttrs(tab)} class="${tabButtonClasses(tab, tab.id === activeTab)}" aria-selected="${tab.id === activeTab ? "true" : "false"}"${tab.id === "tax" && paperAccountsEnabled() ? ` disabled title="Turn off paper accounts to open Tax"` : ""}>${tabButtonInnerHtml(tab, esc(tab.label))}</button>`,
    )
    .join("");
}

function clearInjectedBodyAttrs(): void {
  for (const attr of [
    "data-sfe-focus-mode-active",
    "data-sfe-analytics-plus-active",
    "data-sfe-wheel-lab-active",
    "data-sfe-lab-active",
    "data-sfe-tax-active",
    "data-sfe-pfolio-active",
    "data-sfe-import-active",
    "data-sfe-agents-active",
  ]) {
    document.body.removeAttribute(attr);
  }
}

function setInjectedBodyAttr(tab: WebTabId): void {
  clearInjectedBodyAttrs();
  const attr = bodyAttrForTab(tab);
  if (attr) document.body.setAttribute(attr, "true");
}

function renderHeaderStatusIndicatorsHtml(): string {
  return `
    ${marketStatusIndicatorHtml()}
    ${isRobinhoodIntegrationEnabled() ? robinhoodStatusIndicatorHtml({ label: "RH" }) : ""}
    ${webullStatusIndicatorHtml({ label: "WB" })}
    ${isAgenticFeatureEnabled() ? agentsStatusIndicatorHtml({ label: "AG" }) : ""}
    ${proxyHealthIndicatorHtml({ compact: true, label: "Proxy" })}
  `;
}

function mountWebHeaderStatusIndicators(
  root: HTMLElement,
  onOpenImport?: () => void,
  onOpenAgents?: () => void,
  onRefreshData?: () => void,
  onFeedRefresh?: () => void,
  onOpenWebull?: () => void,
): () => void {
  const host = root.querySelector<HTMLElement>(".sfe-web-header-status");
  if (!host) return () => {};

  host.querySelector('[data-action="proxy-health"]')?.addEventListener("click", () => {
    openProxySettingsModal();
  });

  host.querySelector('[data-action="rh-status"]')?.addEventListener("click", () => {
    onOpenImport?.();
  });

  host.querySelector('[data-action="wb-status"]')?.addEventListener("click", () => {
    onOpenWebull?.();
  });

  host.querySelector('[data-action="agents-status"]')?.addEventListener("click", () => {
    onOpenAgents?.();
  });

  const stopMarket = startMarketStatusPolling(host);
  const stopHealth = startProxyHealthPolling((result) => applyProxyHealth(host, result));
  const stopRobinhood = startRobinhoodStatusPolling((result) => applyRobinhoodStatus(host, result));
  const stopWebull = startWebullStatusPolling((result) => applyWebullStatus(host, result));
  const stopAgents = startAgentsStatusPolling((result) => applyAgentsStatus(host, result));
  const stopFeed = startHeaderFeedIndicator(root, {
    onQuoteRefreshDue: () => {
      if (onFeedRefresh) {
        onFeedRefresh();
        return;
      }
      invalidateOptionsPanelCache();
      onRefreshData?.();
    },
    onRhSnapshotPoll: () => {
      /* RH context polled inside header feed indicator */
    },
    onRhSyncDue: () => {
      if (onFeedRefresh) {
        onFeedRefresh();
        return;
      }
      invalidateOptionsPanelCache();
      onRefreshData?.();
    },
    onOpenImport: () => {
      onOpenImport?.();
    },
  });
  const stopBuyingPower = startHeaderBuyingPower(root, {
    onOpenImport: () => {
      onOpenImport?.();
    },
  });

  return () => {
    stopMarket();
    stopHealth();
    stopRobinhood();
    stopWebull();
    stopAgents();
    stopFeed();
    stopBuyingPower();
  };
}

const WEB_TAB_DEFS = () => filterAgenticTab(WEB_TAB_PREF_DEFINITIONS);

export function mountWebApp(root: HTMLElement): void {
  document.body.classList.add("sfe-standalone-host");
  applyDesktopShellChrome();
  syncWebCompactBodyClass();
  syncTableShadingBodyClass();
  mountFilterResetButtons();
  mountHeaderTableSort();
  bindFocusRowMenuDocumentHandlers();
  mountTableContextMenu();
  bindStockForgeTickerLinks();
  const warmRh = () => warmRobinhoodNetwork();
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(warmRh, { timeout: 2500 });
  } else {
    window.setTimeout(warmRh, 800);
  }
  let tabPrefs = loadWebTabPreferences();
  let activeTab = migrateWebTabId(
    resolveDefaultTabId(WEB_TAB_DEFS(), tabPrefs, loadWebLastTab()),
  ) as WebTabId;
  const contentTabIds = WEB_TAB_BAR.map((tab) => tab.id).filter(
    (id) => id !== "settings" && (!isGatedTabId(id) || isAgenticFeatureEnabled()),
  );

  root.innerHTML = `
    <div class="sfe-web-shell sfe-root">
      ${renderSiteHeaderHtml(
        renderHeaderStatusIndicatorsHtml(),
        headerFeedIndicatorHtml(),
        headerBuyingPowerHtml(),
      )}

      <main class="wrap sfe-web-wheel-view active" id="view-wheel-trade">
        <div class="wheel-subnav-sentinel" aria-hidden="true"></div>
        <div class="panel wheel-subnav sfe-web-wheel-subnav">
          <div class="row wheel-subnav-toolbar">
            <div class="row wheel-subnav-meta">
              <div class="sfe-web-account-scope" data-sfe-account-scope-host></div>
              <div class="row sfe-web-tab-actions">
                <button type="button" class="small ghost sfe-web-compact-btn${isWebCompactMode() ? " active" : ""}" id="sfeWebCompactBtn" aria-label="Compact layout" aria-pressed="${isWebCompactMode() ? "true" : "false"}" data-tip="${isWebCompactMode() ? "Compact layout on — combined columns like mobile. Click to expand." : "Compact layout off — click to combine columns like mobile."}">${webCompactLayoutIcon()}</button>
                <button type="button" class="small ghost sfe-web-settings-btn" data-action="open-settings" id="sfeWebSettingsBtn" aria-label="Settings" data-tip="Settings — account, Stock Forge API key, tabs">⚙</button>
                <button type="button" class="small ghost" id="wheelRefreshBtn" aria-label="Refresh wheel data" data-tip="Refresh data for the current tab">↻</button>
              </div>
            </div>
          </div>
        </div>

        <div class="sfe-web-main">
          ${contentTabIds.map(
            (id) =>
              `<div class="sfe-web-panel panel" data-panel="${id}" hidden="${id !== activeTab ? "true" : "false"}"></div>`,
          ).join("")}
        </div>
      </main>
      ${renderSiteFooterHtml()}
      <nav class="sfe-web-tabbar" aria-label="Wheel strategy">
        <div class="sfe-scroll-edges sfe-web-tab-scroll">
          <div class="row wheel-tab-strip" data-wheel-tabs role="tablist">
            ${renderTabStripHtml(tabPrefs, activeTab)}
          </div>
        </div>
      </nav>
    </div>
  `;

  setAccountScopeSurface(activeTab);
  const scopeHost = root.querySelector<HTMLElement>("[data-sfe-account-scope-host]");
  if (scopeHost) bindAccountScopeBar(scopeHost);
  const tabStrip = root.querySelector<HTMLElement>(".sfe-web-tab-scroll .wheel-tab-strip");
  if (tabStrip) bindScrollEdges(tabStrip);

  const siteHeader = root.querySelector<HTMLElement>("header.top");
  const wheelSubnav = root.querySelector<HTMLElement>(".sfe-web-wheel-subnav");
  const sticky =
    wheelSubnav && siteHeader ? createStickySync(() => activeTab, wheelSubnav, siteHeader) : null;

  const getPanel = (tab: WebTabId): HTMLElement | null =>
    root.querySelector<HTMLElement>(`[data-panel="${tab}"]`);

  const setActiveTabUi = (tab: WebTabId): void => {
    root.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((button) => {
      const tabId = button.dataset.tab as WebTabId;
      const def = findWebTabDef(tabId);
      if (!def || !button.classList.contains("sfe-web-tab")) return;
      applyWheelTabButtonClasses(button, def, tabId === tab);
    });
  };

  const syncTabStrip = (): void => {
    const strip = root.querySelector<HTMLElement>(".wheel-tab-strip");
    if (!strip) return;
    strip.innerHTML = renderTabStripHtml(tabPrefs, activeTab);
    setActiveTabUi(activeTab);
  };
  subscribeAccountScope(() => {
    syncTabStrip();
  });

  const refreshCurrentTab = async (force = false): Promise<void> => {
    const panel = getPanel(activeTab);
    if (!panel) return;
    if (force) {
      await refreshActiveDataTab(panel, activeTab);
    } else {
      await activateWebTab(panel, activeTab);
    }
  };

  const refreshWheelData = async (): Promise<void> => {
    invalidateWheelSnapshots();
    await refreshCurrentTab(true);
  };

  setFocusActionsRefresh(refreshWheelData);
  setWheelLedgerRefresh(refreshWheelData);

  const handleFeedRefresh = (): void => {
    if (activeTab === "options") {
      const panel = getPanel("options");
      if (panel) void refreshNativeOptionsPanelInBackground(panel);
      return;
    }
    if (activeTab === "puts") {
      const panel = getPanel("puts");
      invalidateOptionsPanelCache();
      if (panel) void refreshNativePutsPanelInBackground(panel);
      return;
    }
    if (activeTab === "calls") {
      const panel = getPanel("calls");
      invalidateOptionsPanelCache();
      if (panel) void refreshNativeCallsPanelInBackground(panel);
      return;
    }
    if (activeTab === "puts-plus" || activeTab === "calls-plus") {
      void refreshCurrentTab(true);
      return;
    }
    if (activeTab === "stocks") {
      const panel = getPanel("stocks");
      if (panel) void refreshNativeStocksPanelInBackground(panel);
      return;
    }
    // Market gauges are daily series. Rebuilding this tab on the quote timer
    // wipes the chart window, scroll, and any open note.
    if (activeTab === "market") return;
    // Astrology is a static timing calendar — no need to rebuild on quote ticks.
    if (activeTab === "astrology") return;
    invalidateOptionsPanelCache();
    void refreshCurrentTab(true);
  };

  const stopTabStatus = mountWebHeaderStatusIndicators(
    root,
    () => {
      if (isAgenticFeatureEnabled()) void showTab(IMPORT_TAB_ID);
      else openWebSettingsModal(applyTabPreferences, { section: "robinhood" });
    },
    () => {
      void showTab("agents");
    },
    () => {
      void refreshCurrentTab(true);
    },
    handleFeedRefresh,
    () => {
      openWebSettingsModal(applyTabPreferences, { section: "webull" });
    },
  );

  const showTab = async (tab: WebTabId): Promise<void> => {
    tab = migrateWebTabId(tab) as WebTabId;
    if (tab === "tax" && paperAccountsEnabled()) return;
    if (isGatedTabId(tab) && !isAgenticFeatureEnabled()) return;
    activeTab = tab;
    setAccountScopeSurface(tab);
    saveWebLastTab(tab);
    root.querySelectorAll<HTMLElement>(".sfe-web-panel").forEach((panel) => {
      deactivateWebTab(panel);
    });

    setActiveTabUi(tab);
    setInjectedBodyAttr(tab);
    sticky?.sync();
    if (tab === "agents") markAgentsPageVisited();

    const panel = getPanel(tab);
    if (!panel) return;
    panel.hidden = false;
    await activateWebTab(panel, tab);
  };

  const applyTabPreferences = (next: TabPreferences): void => {
    tabPrefs = next;
    syncTabStrip();
    if (!isTabVisible(WEB_TAB_DEFS(), tabPrefs, activeTab)) {
      activeTab = resolveDefaultTabId(WEB_TAB_DEFS(), tabPrefs, loadWebLastTab()) as WebTabId;
      void showTab(activeTab);
      return;
    }
    setActiveTabUi(activeTab);
  };

  root.querySelector<HTMLElement>(".sfe-web-tabbar")?.addEventListener("click", (event) => {
    if (document.body.classList.contains("sfe-login-required")) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-tab]");
    if (!button || button.disabled) return;
    activeTab = button.dataset.tab as WebTabId;
    void showTab(activeTab);
  });

  root.querySelector<HTMLButtonElement>("#sfeWebSettingsBtn")?.addEventListener("click", () => {
    if (document.body.classList.contains("sfe-login-required")) return;
    openWebSettingsModal(applyTabPreferences);
  });

  const logoutBtn = root.querySelector<HTMLButtonElement>("#sfeWebLogoutBtn");
  const syncLogoutBtn = async (): Promise<void> => {
    if (!logoutBtn) return;
    const signedIn = await isSessionAuthenticated();
    logoutBtn.hidden = !signedIn;
  };
  logoutBtn?.addEventListener("click", () => {
    if (document.body.classList.contains("sfe-login-required")) return;
    void (async () => {
      await logoutSession();
      bindTaxStoreUser(null);
      notifyAuthChanged();
      await syncLogoutBtn();
      openWebLoginModal();
    })();
  });

  registerAuthUiHooks({
    openSettings: () => openWebSettingsModal(applyTabPreferences),
    openLogin: () => openWebLoginModal(),
    onAuthChanged: () => {
      invalidateWheelSnapshots();
      void (async () => {
        await loadAgenticFeatureFlag(true);
        refreshHeaderBuyingPower();
        syncTabStrip();
        if (!isTabVisible(WEB_TAB_DEFS(), tabPrefs, activeTab)) {
          activeTab = resolveDefaultTabId(WEB_TAB_DEFS(), tabPrefs, loadWebLastTab()) as WebTabId;
          await showTab(activeTab);
        }
        await syncTaxStoreToSession();
        await syncLogoutBtn();
        void refreshCurrentTab(true);
      })();
    },
  });

  root.querySelector<HTMLButtonElement>("#wheelRefreshBtn")?.addEventListener("click", () => {
    refreshHeaderBuyingPowerLive();
    void refreshCurrentTab(true);
  });

  const compactBtn = root.querySelector<HTMLButtonElement>("#sfeWebCompactBtn");
  const syncCompactBtn = (): void => {
    if (!compactBtn) return;
    const on = isWebCompactMode();
    compactBtn.classList.toggle("active", on);
    compactBtn.setAttribute("aria-pressed", on ? "true" : "false");
    const tip = on
      ? "Compact layout on — combined columns like mobile. Click to expand."
      : "Compact layout off — click to combine columns like mobile.";
    compactBtn.title = "";
    compactBtn.setAttribute("data-tip", tip);
    compactBtn.setAttribute("aria-label", on ? "Compact layout on" : "Compact layout off");
  };
  syncCompactBtn();
  compactBtn?.addEventListener("click", () => {
    setWebCompactMode(!isWebCompactMode());
    syncCompactBtn();
    void refreshCurrentTab(true);
  });
  void loadAgenticFeatureFlag().then(() => {
    const main = root.querySelector(".sfe-web-main");
    if (main) {
      for (const tab of WEB_TAB_BAR) {
        if (tab.id === "settings") continue;
        if (isGatedTabId(tab.id) && !isAgenticFeatureEnabled()) continue;
        if (root.querySelector(`[data-panel="${tab.id}"]`)) continue;
        const panel = document.createElement("div");
        panel.className = "sfe-web-panel panel";
        panel.dataset.panel = tab.id;
        panel.hidden = true;
        main.appendChild(panel);
      }
    }
    syncTabStrip();
    if (!isTabVisible(WEB_TAB_DEFS(), tabPrefs, activeTab)) {
      activeTab = resolveDefaultTabId(WEB_TAB_DEFS(), tabPrefs, loadWebLastTab()) as WebTabId;
      void showTab(activeTab);
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("agents") === "view") {
      const owner = params.get("owner")?.trim();
      const agent = params.get("agent")?.trim();
      if (owner && agent) {
        const next = new URL("/web/agent-view.html", window.location.origin);
        next.searchParams.set("owner", owner);
        next.searchParams.set("agent", agent);
        window.location.replace(next.href);
        return;
      }
    }
    if (
      (params.get("import") === "connected" || params.get("agentic") === "connected") &&
      isAgenticFeatureEnabled()
    ) {
      activeTab = IMPORT_TAB_ID;
      params.delete("import");
      params.delete("agentic");
      const nextUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", nextUrl);
      void showTab(activeTab);
    }
  });

  void syncTaxStoreToSession().then(() =>
    showTab(activeTab).then(async () => {
      await syncLogoutBtn();
      if (await consumeDesktopPairLink()) return;
      if (consumeAuthDeepLink()) return;
      if (!(await isSessionAuthenticated())) {
        openWebLoginModal();
      }
    }),
  );

  window.addEventListener(
    "beforeunload",
    () => {
      stopTabStatus();
      sticky?.teardown();
    },
    { once: true },
  );
}
